import React, { useEffect, useState, useRef } from 'react';
import { Layout, Loading, Notification, Button, Table, Dialog, MessageBox, Input, Tooltip } from 'element-react';
import { useParams, useHistory, useLocation } from "react-router-dom";
import { API, Auth , graphqlOperation } from 'aws-amplify';
import { Link as ScrollLink } from 'react-scroll';
import ChatIcon from '../components/dashboard/chatIcon';
import NotesIcon from '../components/NotesIcon';
import DeleteIcon from '../components/DeleteIcon';
import { listUsers, listCompanys, getCompany } from '../graphql/queries'; // Asegúrate de que la ruta sea correcta
import userStore from '../store/user'; // Si usas Zustand para el usuario
import PagerQuery from '../utils/PagerQuery';
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api-graphql";
import { useTranslation } from 'react-i18next';
import {generateCompanyChat} from '../services/ChatService';
import { GET_SIGNED_URL, IS_DEV, RFQ_API_URL } from '../utils/Constants'; 
import rfqStore from '../store/rfqStore';

import UploadProposalForm from '../components/UploadProposalForm';
// --- Componente Auxiliar para mostrar los campos ---
const DetailField = ({ label, value, isHighlight = false }) => (
    <Layout.Row className="detail-field">
        <Layout.Col xs={24} sm={5} className="detail-label"><strong>{label}:</strong></Layout.Col>
        <Layout.Col xs={24} sm={19} className={`detail-value ${isHighlight ? 'highlight-red' : ''}`}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{value || '-'}</div>
        </Layout.Col>
    </Layout.Row>
);

// --- Componente Principal ---
function RFQDetail() {
    // --- Estados ---
    const [loading, setLoading] = useState(true);
    const [rfq, setRfq] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [myProposal, setMyProposal] = useState(null);

    const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
    const [currentProposalNote, setCurrentProposalNote] = useState('');
    const [selectedProposalId, setSelectedProposalId] = useState(null);
    const [isNoteEditing, setIsNoteEditing] = useState(false);

    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const {user} = userStore();
    const { t } = useTranslation();

    // --- Funciones de Carga de Datos ---
    const fetchProposals = async (rfqId) => {
        if (IS_DEV) {
            console.log("MODO DESARROLLO: Cargando propuestas locales desde el store.");
            const { mockProposals } = rfqStore.getState();
            
            const relevantProposals = mockProposals.filter(p => p.rfq_id == rfqId);
            
            setProposals(relevantProposals); 
            return;
        }

        // --- CÓDIGO ORIGINAL PARA PRODUCCIÓN ---
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            // Usamos la API "inteligente" que funciona para el comprador
            const response = await fetch(`${RFQ_API_URL}/getPropousalByRfqIdAndUsers/${rfqId}`, {
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': token 
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch proposals');
            }
            
            const data = await response.json();

            // Guardamos los datos reales en el estado
            if (data.proposals) {
                const formattedProposals = data.proposals.map(proposal => ({
                    proposalDate: new Date(proposal.creation_date).toLocaleDateString('en-CA'),
                    proposalId: proposal.id,
                    supplierName: proposal.supplier, 
                    supplierId: proposal.supplier_id,
                    totalCost: `$${parseFloat(proposal.total_cost).toFixed(2)}`,
                    validThrough: new Date(proposal.valid_date).toLocaleDateString('en-CA'),
                    requiredBy: rfq.required_by_date ? new Date(rfq.required_by_date).toLocaleDateString('en-CA') : '-',
                    note: proposal.client_notes, // Para el modal de notas
                    fileKey: proposal.file_key
                }));
                setProposals(formattedProposals);
            }

        } catch (error) {
            console.error("Error fetching proposals:", error);

        }
    };

    const loadSellerProposal = async (rfqId) => {
            if (IS_DEV) {
            console.log("MODO DESARROLLO: Buscando propuesta local para RFQ ID:", rfqId);
            const { mockProposals } = rfqStore.getState();
            
            // Buscamos en el store una propuesta que coincida con el ID del RFQ
            const foundProposal = mockProposals.find(p => p.rfq_id == rfqId);
            
            if (foundProposal) {
                console.log("Propuesta local encontrada:", foundProposal);
                setMyProposal(foundProposal);
            } else {
                console.log("No se encontró propuesta local.");
                setMyProposal(null);
            }
            return;
        }

        // --- CÓDIGO ORIGINAL PARA PRODUCCIÓN ---
        try {
            // Esta función ahora usa la API "inteligente" que te pasaron
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${RFQ_API_URL}/getPropousalByRfqIdAndUsers/${rfqId}`, {
                headers: { 'Authorization': token }
            });
            const data = await response.json();
            // Si la API devuelve una propuesta, la guardamos. Si no, queda en null.
            setMyProposal(data.proposals && data.proposals.length > 0 ? data.proposals[0] : null);
        } catch (error) {
            console.log("No existing proposal found for this user, which is okay.");
            setMyProposal(null);
        }
    };

    const handleDownload = async (fileKey) => {
        if (!fileKey) return;

        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${GET_SIGNED_URL}?fileName=${fileKey}`, {
                headers: { 'Authorization': token }
            });

            if (!response.ok) throw new Error('Failed to get download link.');

            const data = await response.json();
            window.open(data.url, '_blank');

        } catch (error) {
            console.error("Error getting signed URL:", error);
            Notification.error('Could not retrieve file for download.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenNotes = (proposal) => {
        console.log("Abriendo notas para la propuesta:", proposal.proposalId);
        setSelectedProposalId(proposal.proposalId);
        setCurrentProposalNote(proposal.note || '');
        setIsNoteEditing(false); 
        setIsNotesModalVisible(true);
    };
    
    const handleSaveNote = async () => {

        if (currentProposalNote.length > 4000) {
            Notification.error({
                title: 'Error',
                message: 'The note cannot exceed 4000 characters.'
            });
            return; 
        }

        setLoading(true); // Muestra el spinner de carga
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const isSeller = location.state?.isSellerView;

            // 1. Elige la URL correcta según el tipo de usuario
            const apiUrl = isSeller
                ? `${RFQ_API_URL}/updateProposalCreatorNotesById` // URL para el Vendedor
                : `${RFQ_API_URL}/updateProposalClientNotesById`;  // URL para el Comprador

            // 2. Prepara los datos para enviar
            const body = {
                proposalId: selectedProposalId,
                notes: currentProposalNote
            };

            // 3. Llama a la API para guardar la nota
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error('Failed to save note');
            }

            // 4. Cierra el modal y refresca los datos para ver el cambio
            setIsNotesModalVisible(false);
            Notification.success("Note saved successfully!");

            if (isSeller) {
                loadSellerProposal(id); // Refresca los datos de "My Quotation"
            } else {
                fetchProposals(id); // Refresca los datos de "Active Proposals"
            }

        } catch (error) {
            Notification.error("Failed to save note.");
            console.error("Error saving note:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartChat = async (proposal) => {
        setLoading(true);
        try {
            
            const sellerId = proposal.supplierId;

            //1. obtener empresa del vendedor
            const input = { id: sellerId}
            const result  = await API.graphql({
                query: getCompany,
                variables: input,
                authMode: GRAPHQL_AUTH_MODE.AWS_IAM
                });
            console.log({result})
            const companyPage = result.data.getCompany;

            //2. obtener usuario del vendedor
            const filter = {
                cognito_id : {eq : result.data.getCompany.owner}
            }
            const pager = new PagerQuery(listUsers, {filter}, Object.keys({ listUsers }));
            const resultCompanyUser = await pager.searchPrivateData();
            const initialMessage = `Hello, I'd like to discuss your proposal for RFQ #${rfq.id} (${rfq.product_name}).`;
            //3. iniciar chat
            const chatResult  = await generateCompanyChat(initialMessage, companyPage, t, user, resultCompanyUser.data.listUsers.items[0]);
            if(chatResult === true){
                history.push("/my-chats/1");
            }
            setLoading(false);

        } catch (error) {
            console.error("Error starting chat:", error);
            Notification.error("Failed to start the chat. Please try again later.");
            if (error.message === 'The user is not authenticated') {
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProposal = async (proposalId) => {
        console.log("Rechazando la propuesta:", proposalId);
        try {
            await MessageBox.confirm('Are you sure you want to delete this proposal?', 'Confirmation', { 
                type: 'warning' 
            });

            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            await fetch(`${RFQ_API_URL}/updateProposalStatusRfqById`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                body: JSON.stringify({
                    proposalId: proposalId,
                    status: 4
                })
            });

            Notification.success("Proposal rejected.");
            fetchProposals(id); // Recargamos las propuestas para ver el cambio de estado
        } catch (error) {
            if (error !== 'cancel') {
                Notification.error("Failed to reject proposal.");
            }
        }
    };

    // En RFQDetail.js

    useEffect(() => {
        const loadPageData = async () => {
            if (!id) {
                setLoading(false);
                return;
            }
            setLoading(true);

            // --- LÓGICA DE DESARROLLO VS. PRODUCCIÓN ---
            if (IS_DEV) {
                console.log("MODO DESARROLLO: Cargando RFQ local desde el store.");
                
                const { mockRfqs } = rfqStore.getState();

                const foundRfq = mockRfqs.find(item => item.id == id);
                
                if (foundRfq) {
                    const formattedRfq = {
                        id: foundRfq.id,
                        created_date: foundRfq.created_date,
                        status: foundRfq.status,
                        product_name: foundRfq.productName,
                        quantity: foundRfq.quantity,
                        unit: foundRfq.unit,
                        category_name: foundRfq.category ? foundRfq.category[1] : 'N/A',
                        product_description: foundRfq.requirements,
                        certification: `${foundRfq.certification1}|${foundRfq.certification2}`,
                        buying_frequency: foundRfq.buyingFrequency,
                        incoterm: foundRfq.incoterm,
                        destination: foundRfq.destination,
                        shipping_method: foundRfq.shippingMethod,
                        required_by_date: foundRfq.requiredDate,
                        expire_in: foundRfq.expiredIn
                    };
                    setRfq(formattedRfq);
                } else {
                    setRfq(null); // No se encontró el RFQ en el store
                }

                // En modo desarrollo, no hay propuestas reales, así que dejamos las listas vacías
                setProposals([]);
                setMyProposal(null);
                setLoading(false);
                return; // Detenemos la función aquí
            }

            // --- CÓDIGO ORIGINAL PARA PRODUCCIÓN ---

            try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const isSeller = location.state?.isSellerView;

                // Carga los detalles principales del RFQ (esto es para ambos)
                const rfqResponse = await fetch(`${RFQ_API_URL}/getRFQByOwnerAndId/${id}`, {
                    headers: { 'Authorization': token }
                });
                if (!rfqResponse.ok) throw new Error('Failed to load RFQ Details');
                const rfqData = await rfqResponse.json();
                
                if (rfqData.returnCode === 0 && rfqData.rfq) {
                    setRfq(rfqData.rfq);
                } else {
                    throw new Error(rfqData.returnMessage || 'RFQ not found');
                }

                // carga las propuestas asociadas usando la API "inteligente"
                const proposalsResponse = await fetch(`${RFQ_API_URL}/getPropousalByRfqIdAndUsers/${id}`, {
                    headers: { 'Authorization': token }
                });
                if (!proposalsResponse.ok) throw new Error('Failed to load proposals');

                const proposalsData = await proposalsResponse.json();
                
                if (proposalsData.proposals) {
                    if (isSeller) {
                        // Si es vendedor, esto encontrará su propuesta si existe y actualizará la vista
                        setMyProposal(proposalsData.proposals[0] || null);
                    } else {
                        const activeProposals = proposalsData.proposals.filter(p => p.status_rfq !== 4);
                        
                        const formattedProposals = activeProposals.map(p => ({
                            proposalDate: new Date(p.creation_date).toLocaleDateString('en-CA'),
                            proposalId: p.id,
                            supplierName: p.supplier,
                            supplierId: p.supplier_id,
                            totalCost: `$${parseFloat(p.total_cost).toFixed(2)}`,
                            validThrough: new Date(p.valid_date).toLocaleDateString('en-CA'),
                            note: p.client_notes,
                            fileKey: p.file_key
                        }));
                        setProposals(formattedProposals);
                    }
                }
            } catch (error) {
                console.error("Error loading page data:", error);
                setRfq(null);
            } finally {
                setLoading(false);
            }
        };

        loadPageData();
    }, [id, location.state]);

    const handleProposalSubmitted = () => {
        loadSellerProposal(id);
    };

    const proposalsColumns = [
        { label: 'Proposal Date', prop: 'proposalDate', width: 135 },
        { label: 'Id', prop: 'proposalId', width: 90 },
        {
            label: 'Supplier',
            prop: 'supplierName',
            render: (row) => (
                <Tooltip content={row.supplierName} placement="bottom-start">
                    <span className="product-name truncate-text">{row.supplierName}</span>
                </Tooltip>
            )
        },
        { label: 'Total Cost', prop: 'totalCost', width: 110 },
        {
            label: 'Proposal',
            width: 100,
            render: (row) => (
                row.fileKey 
                    ? <Button type="text" onClick={() => handleDownload(row.fileKey)}>Download</Button> 
                    : '–'
            )
        },
        { label: 'Valid Through', prop: 'validThrough', width: 138 },
        {
            label: 'Actions',
            width: 100,
            align: 'center',
            render: (row) => (
                <div className="action-icons" onClick={(e) => e.stopPropagation()}>
                    <Tooltip content="Start Chat" placement="bottom">
                        <ChatIcon handleClick={() => handleStartChat(row)} className="svgChatIcon" width="25px"/>
                    </Tooltip>
                    <Tooltip content="Notes" placement="bottom">
                        <NotesIcon color='#FF6D73' handleClick={() => handleOpenNotes(row)} width="15px"/>
                    </Tooltip>
                    <Tooltip content="Delete" placement="bottom">
                        <DeleteIcon handleClick={() => handleDeleteProposal(row.proposalId)} className="svgDeleteIcon" width="32px" />
                    </Tooltip>
                </div>
            )
        }
    ];

    const MyQuotationTable = ({ proposal, onOpenNotes }) => {

        const columns = [
            { label: 'Proposal Date', prop: 'proposalDate', width: 135 },
            { label: 'Id', prop: 'proposalId', width: 85 },
            { label: 'Product', prop: 'productName', 
                render: (row) => (
                    <Tooltip content={row.productName} placement="bottom-start">
                        <span className="product-name truncate-text">{row.productName}</span>
                    </Tooltip>
                )  },
            { label: 'Total Cost', prop: 'totalCost', width: 110 },
            { 
                label: 'Proposal', 
                width: 120, 
                render: (row) => (
                    row.fileKey
                        ? <Button type="text" onClick={() => handleDownload(row.fileKey)}>Download</Button>
                        : '–'
                )
            },
            { label: 'Valid Through', prop: 'validThrough', width: 140 },
            { label: 'Required By', prop: 'requiredBy', width: 125 },
            {
                label: 'Actions',
                width: 100,
                align: 'center',
                render: (row) => (
                    <div className="action-icons" onClick={(e) => e.stopPropagation()}>
                        <Tooltip content="Notes" placement="bottom">
                            <NotesIcon color="#FF6D73" handleClick={() => onOpenNotes(row)} width={20}/>
                        </Tooltip>
                        <Tooltip content="Delete" placement="bottom">
                            <DeleteIcon handleClick={() => handleDeleteMyProposal(row.proposalId)} width={40} paddingTop={0}/>
                        </Tooltip>
                    </div>
                )
            }
        ];

        const tableData = [{
            proposalDate: new Date(proposal.creation_date).toLocaleDateString('en-CA'), 
            proposalId: proposal.id,                      
            productName: rfq.product_name,                
            totalCost: `$${parseFloat(proposal.total_cost).toFixed(2)}`, 
            validThrough: new Date(proposal.valid_date).toLocaleDateString('en-CA'),  
            requiredBy: rfq.required_by_date ? new Date(rfq.required_by_date).toLocaleDateString('en-CA') : '-',
            note: proposal.creator_notes,
            fileKey: proposal.file_key
        }];

        return (
            <div className="my-quotation-container">
                <h3>My Quotation</h3>
                <div className="table-container responsive-table-container" style={{ marginTop: '20px' }}>
                    <Table
                        className="rfq-table" 
                        data={tableData}
                        columns={columns}    
                        fit={true}
                        emptyText="No proposal data available."
                    />
                </div>
            </div>
        );
    };

    if (loading) return <Loading fullscreen={true} />;
    if (!rfq) return <div style={{ padding: '20px' }}>RFQ not found or you don't have permission to view it.</div>;

    const handleDeleteMyProposal = async (proposalId) => {
        try {
            await MessageBox.confirm('Are you sure you want to delete this proposal?', 'Confirmation', { 
                type: 'warning' 
            });

            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            // Llamamos a la API para que el Vendedor actualice el estado de su propuesta
            const response = await fetch(`${RFQ_API_URL}/updateProposalStatusById`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify({
                    proposalId: proposalId,
                    status: 3 // Usamos estatus 3 para "Retirado por el vendedor"
                })
            });

            if (!response.ok) {
                throw new Error('API request to withdraw proposal failed');
            }

            setMyProposal(null);
            Notification.success('Your proposal has been successfully withdrawn!');

        } catch (error) {
            if (error !== 'cancel') {
                console.error("Error withdrawing proposal:", error);
                Notification.error('Failed to withdraw your proposal.');
            }
        } finally {
            setLoading(false);
        }
    };

    // --- JSX Principal ---
    return (
        <div className="rfq-detail-container">
            <div className="page-header">
                <div className="header-content">
                    <h1><span className="title-red">RFQ</span><span className="title-dark">Direct</span></h1>
                    <h2 className="subtitle">
                        {location.state?.isSellerView ? 'Active Request For Quotation' : 'My Request For Quotation'}
                    </h2>
                </div>
                <div className="actions-group"> 
                    {/* El nuevo botón solo aparece si es la vista del comprador */}
                    {!location.state?.isSellerView && (
                        <ScrollLink to="proposals-section" spy={true} smooth={true} offset={-100} duration={500}>
                            <Button type="primary">
                                View Proposals
                            </Button>
                        </ScrollLink>
                    )}
                    {location.state?.isSellerView && (
                        <ScrollLink to="upload-form-section" spy={true} smooth={true} offset={-100} duration={500}>
                            <Button type="primary">Submit Proposal</Button>
                        </ScrollLink>
                    )}
                    <Button type="primary" onClick={() => history.goBack()}>
                        Go Back
                    </Button>
                </div>
                
            </div>

            <div className="detail-card">
                <DetailField label="RFQ ID" value={'R' + rfq.id} />
                <DetailField label="Date" value={new Date(rfq.created_date).toLocaleDateString('en-CA')} />
                <DetailField label="Status" value={rfq.status === 1 ? 'Active' : 'Inactive'} />
                <hr className="divider" />
                <DetailField label="Product Name" value={rfq.product_name} isHighlight={true} />
                <DetailField label="Quantity" value={rfq.quantity} />
                <DetailField label="Unit" value={rfq.unit} />
                <DetailField label="Product Category" value={rfq.category_name} />
                <DetailField label="Product Requirements" value={rfq.product_description} />
                <DetailField label="Certifications" value={rfq.certification} />
                <DetailField 
                    label="Attachment" 
                    value={
                        rfq.file_key 
                            ? <Button type="text" onClick={() => handleDownload(rfq.file_key)}>Download File</Button> 
                            : '-'
                    } 
                />
                <hr className="divider" />
                <DetailField label="Buying Frequency" value={rfq.buying_frequency} />
                <DetailField label="Incoterm" value={rfq.incoterm} />
                <DetailField label="Destination" value={rfq.destination} />
                <DetailField label="Shipping Method" value={rfq.shipping_method} />
                <DetailField label="Required By Date" value={rfq.required_by_date ? new Date(rfq.required_by_date).toLocaleDateString('en-CA') : '-'} />
                <DetailField label="Expires In" value={rfq.expire_in} />
            </div>

            {!location.state?.isSellerView && (
                <div id="proposals-section"  className="proposals-container" style={{ marginTop: '40px' }}>
                    <h3>Active Proposals</h3>
                    <div className="table-container responsive-table-container" style={{ marginTop: '20px' }}>
                        <Table
                            className="rfq-table"
                            data={proposals}
                            columns={proposalsColumns}
                            fit={true}
                            emptyText="No proposals received yet."
                        />
                    </div>
                </div>
            )}
            {location.state?.isSellerView && (
                <div id="upload-form-section" style={{ marginTop: '40px' }}>
                    {myProposal ? (
                        <MyQuotationTable proposal={myProposal} onOpenNotes={handleOpenNotes}/>
                    ) : (
                        <UploadProposalForm rfq={rfq} onProposalSubmit={handleProposalSubmitted} />
                    )}
                </div>
            )}

            {/* --- MODAL --- */}
            <Dialog
                title={`Notes for Proposal #${selectedProposalId || ''}`}
                visible={isNotesModalVisible}
                onCancel={() => setIsNotesModalVisible(false)}
                customClass="responsive-dialog"
            >
                <Dialog.Body>
                    {isNoteEditing ? (
                        <>
                            <Input
                                type="textarea"
                                autosize={{ minRows: 5, maxRows: 8 }}
                                placeholder="Add a note..."
                                value={currentProposalNote}
                                onChange={setCurrentProposalNote}
                            />
                            <div style={{ 
                                textAlign: 'right', 
                                fontSize: '12px', 
                                marginTop: '5px',
                                color: currentProposalNote.length > 4000 ? 'red' : '#888' 
                            }}>
                                {currentProposalNote.length} / 4000
                            </div>
                        </>
                    ) : (
                        <p style={{ minHeight: '115px', whiteSpace: 'pre-wrap' }}>
                            {currentProposalNote || ''}
                        </p>
                    )}
                </Dialog.Body>

                <Dialog.Footer className="dialog-footer">
                    {isNoteEditing ? (
                        <>
                            <Button type="secondary" className='btn-edit-rfq' onClick={() => setIsNoteEditing(false)}>Cancel</Button>
                            <Button type="primary" onClick={handleSaveNote}>Save</Button>
                        </>
                    ) : (
                        <>
                            <Button type="secondary" className='btn-edit-rfq' onClick={() => setIsNoteEditing(true)}>Edit</Button>
                            <Button type="primary" onClick={() => setIsNotesModalVisible(false)}>OK</Button>
                        </>
                    )}
                </Dialog.Footer>
            </Dialog>
            {/* --- FIN DEL MODAL --- */}
        </div>
    );
}

export default RFQDetail;